import {Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from "@angular/core";
import {UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {VehicleService} from "../../services";
import {FORM_INPUT_REQS} from "src/app/app.config";
import {Actions, ofType} from "@ngrx/effects";
import {combineLatest, Subject} from "rxjs";
import {take, takeUntil} from "rxjs/operators";
import * as vehicleActions from "src/app/clearpath-module/store/actions/vehicle.actions";
import {ActivatedRoute, NavigationExtras, Router} from "@angular/router";
import {HistoryService} from "../../services/history.service";
import swal, { SweetAlertOptions } from "sweetalert2";

@Component({
  selector: "app-stock-number",
  templateUrl: "./stock-number.component.html",
  styleUrls: ["./stock-number.component.scss"]
})
export class StockNumberComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject();
  @Output() formSubmitFlag = new EventEmitter();
  @ViewChild('stockNumberInput') stockNumberInput: ElementRef;
  error = "";
  currentStockNumber = '';
  newStockNumber = '';

  public REQS = FORM_INPUT_REQS;
  stockNumberForm: UntypedFormGroup = this.formBuilder.group({
    stockNumber: [""],
    odometer: [null, Validators.min(0)],
  });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private vehicleService: VehicleService,
    private router: Router,
    private actions$: Actions,
    private activatedRoute: ActivatedRoute,
    private historyService: HistoryService,
  ) {
  }

  ngOnInit() {

    combineLatest([
      this.vehicleService.selectVehicle()
    ])
      .pipe(take(1))
      .subscribe(([vehicle]) => {
        this.currentStockNumber = vehicle.stockNumber;
      });

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(params => {
        if (params.stockNumber !== "") {
          this.stockNumberForm.patchValue({stockNumber: params.stockNumber});
        }
      });

    this.actions$.pipe(
      takeUntil(this.unsubscribe$),
      ofType(
        vehicleActions.changeStockNumberSuccess,
        vehicleActions.changeStockNumberFailure
      ),
    )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((action) => {
        swal.close(); // Close loading indicator
        
        if ('error' in action) {
          if (
            action.error === 'stock_number_not_available_for_sale' ||
            action.error === 'stock_number_used_missing_category'
          ) {
            this.vehicleService.displayVehicleUnavailableDialog(action.error, this.newStockNumber).then(() => {
              // Focus on stock number input after alert closes
              if (this.stockNumberInput) {
                this.stockNumberInput.nativeElement.focus();
                this.stockNumberInput.nativeElement.select();
              }
            });
            // Clear the error so it doesn't show inline
            this.error = "";
          } else {
            // Show other errors inline as before
            this.error = action.error;
          }
        } else {
          // console.log("Stock number found:", this.newStockNumber);
          this.historyService.dispatchAddEvent({
            shortName: "Stock Number Changed",
            oldValue: this.currentStockNumber,
            newValue: this.newStockNumber,
          });
          this.formSubmitFlag.emit();
        }
      });

  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  openVehicleLookup() {
    const navigationExtras: NavigationExtras = {
      queryParams: {changeDealOnSelectVehicle: false, dispatchStockNumberOnly: true,},
    };
    this.router.navigate([{
      outlets: {
        modal: ["vehicle-lookup"]
      },
    }], navigationExtras);
  }

  onSubmitForm() {
    const {stockNumber, odometer} = this.stockNumberForm.value;
    this.newStockNumber = stockNumber;
    
    // Show loading indicator
    swal.fire({
      title: 'Checking Vehicle Availability',
      html: '<div class="swal-spinner"></div><div style="margin-top: 10px;">Please wait...</div>',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showClass: {
        popup: '',
        backdrop: 'swal2-noanimation'
      },
      hideClass: {
        popup: ''
      }
    });
    
    this.vehicleService.dispatchChangeStockNumber(stockNumber, odometer);
  }

  renderErrorMessage(code: any) {
    switch (code) {
      case "stock_number_not_found":
        return "Stock number not found.";
      case "stock_number_not_available_for_sale":
        return "Stock number not available for sale.";
      case "stock_number_used_missing_category":
        return "Stock number is marked as used and is missing category."
    }
    return code;
  }
}
